/**
 * useElevation() — theme-aware shadow-border ("de-border") elevation hook.
 *
 * Mirrors `useFormStyles` / `useWizardStyles`: it reads the live AntD theme via
 * `theme.useToken()`, detects light-vs-dark from the resolved `colorBgContainer`,
 * and returns ready-to-spread `CSSProperties` built from the `elevationTokens`
 * (tokens.ts) + `radiusTokens` + `focusRingTokens`. It is a DIRECT-IMPORT hook,
 * NOT an AntD theme token, because `theme.useToken()` cannot surface arbitrary
 * custom tokens (radius still threads into `theme.ts` so AntD primitives round on
 * the new scale — this hook covers the bespoke surfaces AntD does not own).
 *
 * ── When to reach for each returned style ──────────────────────────────────
 *   popover  Elevated FLOATING panel (e.g. the quick-create popover). Strong
 *            layered shadow + a WCAG-compliant `ring` hairline + `panel` radius (12).
 *   card     Resting GROUPING card / inner surface (admin panel, detail card).
 *            Soft de-border shadow carries the elevation; a decorative `ringSubtle`
 *            edge only. `card` radius (8).
 *   control  Flat-chrome INTERACTIVE control (input/select/button chrome that must
 *            read as a control). WCAG-compliant `ring` border, no shadow. `control`
 *            radius (6).
 *   ringOnly Just the WCAG-compliant `ring` as a 1px border — no shadow, no radius
 *            override. For a divider/edge that needs a perceivable boundary alone.
 *   focusRing Keyboard-focus outline, derived from the purple `colorPrimary`
 *            (2px solid, 2px offset).
 *
 * ── Ring vs shadow vs both ─────────────────────────────────────────────────
 *   - Surfaces that are UI components / floating overlays and must satisfy WCAG
 *     1.4.11 (Non-text Contrast) carry the `ring` (control / popover / ringOnly).
 *   - A large decorative grouping card leans on the soft SHADOW for its boundary
 *     and only paints a faint `ringSubtle` edge (aesthetic, not a compliance edge).
 *   - `focusRing` is a separate concern (keyboard focus), always the purple.
 *
 * ── Concentric rule ────────────────────────────────────────────────────────
 *   A rounded element nested inside a rounded, padded container should round on
 *   `innerRadius(outer, padding)` (tokens.ts) so corners stay concentric — e.g. a
 *   `card`-radius (8) inner field inside a `panel`-radius (12) popover padded by 4.
 *   The scale is authored so panel(12) ⊃ card(8) ⊃ control(6) ⊃ chip(4).
 *
 * ── WCAG contract ──────────────────────────────────────────────────────────
 *   `ring` is the compliance element: a 1px hairline that INDEPENDENTLY clears
 *   3:1 vs the mode's `colorBgContainer` AND `colorBgElevated` (proven in
 *   `__tests__/elevation.wcag.test.ts`, the hard gate). `ringSubtle` is explicitly
 *   decoration-only (1.4.11 decoration exemption) and is NOT gated — never use it
 *   as the sole boundary of an interactive control.
 *
 * Usage:
 * ```tsx
 * const elevation = useElevation();
 * <div style={{ ...elevation.popover }}>…quick-create panel…</div>
 * <input style={{ ...elevation.control }} />
 * ```
 */

import { theme } from 'antd';
import type { CSSProperties } from 'react';

import { elevationTokens, focusRingTokens, radiusTokens } from './tokens';

/** The five elevation surfaces the hook resolves for the active theme mode. */
export interface ElevationStyles {
  /** Elevated floating panel: layered shadow + WCAG ring + panel radius. */
  popover: CSSProperties;
  /** Resting grouping card: soft de-border shadow + decorative ringSubtle + card radius. */
  card: CSSProperties;
  /** Flat-chrome interactive control: WCAG ring border + control radius. */
  control: CSSProperties;
  /** Bare WCAG-compliant ring (1px border only) for divider/edge use. */
  ringOnly: CSSProperties;
  /** Keyboard-focus outline derived from the purple colorPrimary. */
  focusRing: CSSProperties;
}

/** Standard WCAG relative luminance for an `#rrggbb` hex (0 = black, 1 = white). */
function relativeLuminance(hex: string): number {
  const v = hex.replace('#', '');
  const channels = [0, 2, 4].map((i) => parseInt(v.slice(i, i + 2), 16) / 255);
  const lin = channels.map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

/**
 * Detect the theme mode from the resolved container background: a dark surface
 * (luminance < 0.5) selects the dark elevation set, otherwise light. Non-hex
 * inputs (unexpected) fall back to light so the boundary never disappears.
 */
function isDarkSurface(colorBgContainer: string): boolean {
  if (!/^#[0-9a-f]{6}$/i.test(colorBgContainer)) return false;
  return relativeLuminance(colorBgContainer) < 0.5;
}

/**
 * Pure resolver — the hook's testable core (no React context needed). Given the
 * resolved container background and primary color, returns the elevation styles
 * for the matching mode. `useElevation()` simply feeds it `theme.useToken()`.
 */
export function resolveElevation(colorBgContainer: string, colorPrimary: string): ElevationStyles {
  const mode = isDarkSurface(colorBgContainer) ? elevationTokens.dark : elevationTokens.light;

  return {
    popover: {
      borderRadius: radiusTokens.panel,
      border: `1px solid ${mode.ring}`,
      boxShadow: mode.shadow,
    },
    card: {
      borderRadius: radiusTokens.card,
      border: `1px solid ${mode.ringSubtle}`,
      boxShadow: mode.shadow,
    },
    control: {
      borderRadius: radiusTokens.control,
      border: `1px solid ${mode.ring}`,
    },
    ringOnly: {
      border: `1px solid ${mode.ring}`,
    },
    focusRing: {
      outline: `${focusRingTokens.width}px solid ${colorPrimary}`,
      outlineOffset: focusRingTokens.offset,
    },
  };
}

/**
 * Theme-aware elevation hook. Resolves the shadow-border styles for the active
 * light/dark theme from the live AntD token set.
 */
export function useElevation(): ElevationStyles {
  const { token } = theme.useToken();
  return resolveElevation(token.colorBgContainer, token.colorPrimary);
}

export default useElevation;
